import React, { useState, useEffect, useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, Filler } from 'chart.js';
import api, { trackActivity, portfolioRegistryAPI } from '../services/api';
import { buildDisplayNameMap, buildPortfolioCategories } from '../utils/portfolioNames';
import StressTest from '../components/StressTest';
import './RiskModelApp.css';
import ManagedDisclaimer from '../components/ManagedDisclaimer';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, Filler);

// Distinct palette for fund-level attribution charts
const FUND_PALETTE = [
  '#072040', '#f0a500', '#599BE3', '#91CF9B', '#c0392b',
  '#8e44ad', '#e67e22', '#16a085', '#2980b9', '#d35400',
  '#27ae60', '#e74c3c', '#1abc9c', '#9b59b6', '#f39c12',
];

const SAC_COLORS = {
  EQ_AU:      '#072040',
  EQ_WORLD:   '#599BE3',
  EQ_EM:      '#f0a500',
  Cash:       '#91CF9B',
  BND_AU:     '#4a90d9',
  BND_US:     '#6c757d',
  CREDIT_AU:  '#003366',
  CREDIT_GL:  '#0055aa',
  CURRENCY:   '#ffc107',
  HF_GM:      '#c0392b',
  HF_LS:      '#e74c3c',
  INFRA:      '#27ae60',
  PE:         '#8e44ad',
  REITs:      '#e67e22',
  ALT_DEF:    '#16a085',
};


// Colour helper for factor beta cells
const getBetaColor = (value) => {
  if (value == null) return 'transparent';
  const absVal = Math.min(Math.abs(value), 5);
  const intensity = 0.1 + (absVal / 5) * 0.7;
  if (value > 0) return `rgba(89, 155, 227, ${intensity})`;   // info-color blue
  if (value < 0) return `rgba(231, 76, 60, ${intensity})`;    // red
  return 'transparent';
};

// Colour-code correlation values: blue for positive, red for negative, stronger = more opaque
const getCorrelationColor = (value) => {
  if (value == null || value === 1) return 'transparent';
  const abs = Math.min(Math.abs(value), 1);
  const intensity = 0.08 + abs * 0.72;
  if (value > 0) return `rgba(89, 155, 227, ${intensity})`;
  if (value < 0) return `rgba(231, 76, 60, ${intensity})`;
  return 'transparent';
};

// Abbreviate factor names for column headers
const abbreviateFactorName = (name) => {
  if (name.startsWith('Style_')) return 'S:' + name.substring(6);
  if (name.startsWith('Tilt_')) return 'T:' + name.substring(5);
  return name;
};


const RiskModelApp = () => {
  const [availablePortfolios, setAvailablePortfolios] = useState([]);
  const [portfolioRegistry, setPortfolioRegistry] = useState(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState('');
  const [saaYears, setSaaYears] = useState([]);          // available SAA vintages (desc)
  const [activeSaaFY, setActiveSaaFY] = useState(null);  // the live SAA year
  const [saaVintage, setSaaVintage] = useState(null);    // selected vintage (defaults to live)
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPercentile, setSelectedPercentile] = useState('p50');
  const [activeTab, setActiveTab] = useState('analysis');
  const [attribView, setAttribView] = useState('funds');     // 'funds' | 'factors'
  const [attribWindow, setAttribWindow] = useState('monthly'); // 'monthly' | 'cumulative' | '3' | '6' | '12'
  const [drillFund, setDrillFund] = useState('');            // selected fund name for drill-down
  const [expHistView, setExpHistView] = useState('factor');  // 'fund' | 'factor'
  const [expHistFactor, setExpHistFactor] = useState({});    // {sac: selectedFactor}
  // Portfolio Builder state
  const [builderHoldings, setBuilderHoldings] = useState(null);  // {EQ_AU:[{tr_code,name,weight%}], EQ_INTL:[...]}
  const [builderResults, setBuilderResults] = useState({ EQ_AU: null, EQ_INTL: null });
  const [builderLoading, setBuilderLoading] = useState(false);
  const [builderError, setBuilderError] = useState(null);
  const [availableFunds, setAvailableFunds] = useState([]);
  const [builderAddSel, setBuilderAddSel] = useState({ EQ_AU: '', EQ_INTL: '' });
  const [builderAddWt, setBuilderAddWt] = useState({ EQ_AU: '', EQ_INTL: '' });
  const [builderInitialized, setBuilderInitialized] = useState(false);
  const [builderSac, setBuilderSac] = useState('EQ_AU');

  // Initialise Portfolio Builder holdings when tab becomes active
  useEffect(() => {
    if (activeTab !== 'builder' || !results?.factor_exposures || builderInitialized) return;
    const init = { EQ_AU: [], EQ_INTL: [] };
    const SAC_MAP = { EQ_AU: 'EQ_AU', EQ_INTL: 'EQ_INTL', EQ_WORLD: 'EQ_INTL', EQ_EM: 'EQ_INTL' };
    Object.entries(results.factor_exposures.sac_groups || {}).forEach(([sac, funds]) => {
      const target = SAC_MAP[sac];
      if (!target) return;
      funds.forEach(f => {
        init[target].push({ tr_code: f.tr_code, name: f.fund_name, weight: parseFloat((f.weight || 0).toFixed(2)) });
      });
    });
    setBuilderHoldings(init);
    setBuilderInitialized(true);
    if (availableFunds.length === 0) {
      api.get('/api/risk-model/available-equity-funds').then(r => setAvailableFunds(r.data)).catch(() => {});
    }
  }, [activeTab, results, builderInitialized]);

  useEffect(() => {
    trackActivity('app_access', 'risk_model');
    Promise.all([
      api.get('/api/risk-model/available-portfolios'),
      portfolioRegistryAPI.getRegistry('risk_model').catch(() => null),
      api.get('/api/risk-model/saa-vintages').catch(() => null),
    ])
      .then(([availableRes, registryRes, vintagesRes]) => {
        setAvailablePortfolios(availableRes.data);
        if (registryRes) setPortfolioRegistry(registryRes);
        if (vintagesRes?.data) {
          const fys = vintagesRes.data.financial_years || [];
          setSaaYears(fys);
          setActiveSaaFY(vintagesRes.data.active_financial_year ?? null);
          setSaaVintage(vintagesRes.data.active_financial_year ?? fys[0] ?? null);
        }
      })
      .catch((err) => {
        console.error('Error fetching portfolios:', err);
        setError('Failed to load portfolios. Please check the backend is running.');
      });
  }, []);

  const availableKeys = useMemo(
    () => new Set(availablePortfolios),
    [availablePortfolios]
  );

  const displayNames = useMemo(
    () => portfolioRegistry ? buildDisplayNameMap(portfolioRegistry) : {},
    [portfolioRegistry]
  );
  const getDisplayName = (key) => displayNames[key] || key;

  const categories = useMemo(
    () => buildPortfolioCategories(portfolioRegistry, availableKeys),
    [portfolioRegistry, availableKeys]
  );

  // Run the risk-model calculation for a portfolio + SAA vintage (financial_year).
  // financialYear=null benchmarks against the live SAA.
  const runCalculation = (portfolio, financialYear) => {
    if (!portfolio) {
      setResults(null);
      return;
    }
    setIsLoading(true);
    setError(null);

    api
      .post('/api/risk-model/calculate', { portfolio, financial_year: financialYear ?? null })
      .then((res) => {
        setResults(res.data);
        // Reset portfolio builder when new results are loaded
        setBuilderInitialized(false);
        setBuilderHoldings(null);
        setBuilderResults({ EQ_AU: null, EQ_INTL: null });
      })
      .catch((err) => {
        console.error('Calculation error:', err);
        setError('Calculation failed. Please try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePortfolioChange = (e) => {
    const portfolio = e.target.value;
    setSelectedPortfolio(portfolio);
    runCalculation(portfolio, saaVintage);
  };

  const handleVintageChange = (e) => {
    const fy = parseInt(e.target.value, 10);
    setSaaVintage(fy);
    if (selectedPortfolio) runCalculation(selectedPortfolio, fy);
  };

  // Sort weights by value descending for display, exclude zero weights
  const sortedWeights = results?.weights
    ? Object.entries(results.weights).filter(([, v]) => v !== 0).sort((a, b) => b[1] - a[1])
    : [];

  // SAA Tilts bar chart data
  const tiltsChartData = useMemo(() => {
    if (!results?.saa_tilts?.latest) return null;
    const entries = Object.entries(results.saa_tilts.latest).filter(
      ([, val]) => val !== 0
    );
    if (entries.length === 0) return null;

    const labels = entries.map(([ac]) => displayNames[ac] || ac);
    const values = entries.map(([, val]) => val);

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: values.map((v) =>
            v >= 0 ? 'rgba(89, 155, 227, 0.8)' : 'rgba(240, 165, 0, 0.8)'
          ),
          borderColor: values.map((v) =>
            v >= 0 ? '#599BE3' : '#f0a500'
          ),
          borderWidth: 1,
          borderRadius: 3,
        },
      ],
    };
  }, [results?.saa_tilts, displayNames]);

  const tiltsChartOptions = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.parsed.x >= 0 ? '+' : ''}${ctx.parsed.x.toFixed(1)}pp`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: 'rgba(0,0,0,0.06)' },
          ticks: {
            callback: (v) => `${v >= 0 ? '+' : ''}${v}`,
            font: { size: 11 },
          },
        },
        y: {
          grid: { display: false },
          ticks: { font: { size: 11 } },
        },
      },
    }),
    []
  );

  return (
    <div className="container-fluid mt-4 fade-in risk-model-container" style={{ paddingLeft: '2rem', paddingRight: '2rem' }}>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} />
        </div>
      )}

      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <h2 className="mb-1">Risk Model</h2>
          <p className="text-muted">Sub-Asset Class Risk Decomposition</p>
        </div>
      </div>

      <div className="row">
        {/* Left Column */}
        <div className="col-lg-4">
          {/* Portfolio Selector */}
          <div className="card mb-4 portfolio-select-card">
            <div className="card-header">
              <h5>Select Portfolio</h5>
            </div>
            <div className="card-body">
              <select
                className="portfolio-select"
                value={selectedPortfolio}
                onChange={handlePortfolioChange}
                disabled={isLoading}
              >
                <option value="">-- Select a portfolio --</option>
                {categories && categories.length > 0 ? (
                  categories.map((cat) => {
                    const options = cat.keys.filter((k) => availableKeys.has(k));
                    if (options.length === 0) return null;
                    return (
                      <optgroup key={cat.label} label={cat.label}>
                        {options.map((k) => (
                          <option key={k} value={k}>{getDisplayName(k)}</option>
                        ))}
                      </optgroup>
                    );
                  })
                ) : (
                  availablePortfolios.map((p) => (
                    <option key={p} value={p}>{getDisplayName(p)}</option>
                  ))
                )}
              </select>
              {saaYears.length > 0 && (
                <div className="d-flex align-items-center gap-2 mt-3">
                  <label className="mb-0" style={{ fontSize: '0.85rem', color: 'var(--gray-color)' }}>SAA vintage</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 150 }}
                    value={saaVintage ?? ''}
                    onChange={handleVintageChange}
                    disabled={isLoading}
                    title="Which SAA financial year to benchmark tilts against"
                  >
                    {saaYears.map((fy) => (
                      <option key={fy} value={fy}>FY{fy}{fy === activeSaaFY ? ' (live)' : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              {isLoading && (
                <div className="text-center mt-3">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2 text-muted">Calculating...</span>
                </div>
              )}
            </div>
          </div>

          {/* Sub-Asset Class Weights */}
          {sortedWeights.length > 0 && (
            <div className="card mb-4">
              <div className="card-header" style={{ backgroundColor: '#072040', color: '#fff' }}>
                <h5 style={{ margin: 0, fontSize: '1rem' }}>Sub-Asset Class Weights</h5>
              </div>
              <div className="card-body p-0">
                <table className="table table-sm mb-0 weights-table">
                  <thead>
                    <tr>
                      <th>Asset Class</th>
                      <th className="text-end">Weight</th>
                      <th className="weight-bar-cell"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedWeights.map(([ac, weight]) => (
                      <tr key={ac}>
                        <td>{ac}</td>
                        <td className="text-end">{(weight * 100).toFixed(1)}%</td>
                        <td className="weight-bar-cell">
                          <div className="weight-bar-bg">
                            <div
                              className="weight-bar-fill"
                              style={{ width: `${weight * 100}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SAA Tilts */}
          {tiltsChartData && (
            <div className="card mb-4">
              <div className="card-header" style={{ backgroundColor: '#072040', color: '#fff' }}>
                <h5 style={{ margin: 0, fontSize: '1rem' }}>
                  SAA Tilts
                  {results?.tracking_error && (
                    <span className="te-badge">
                      TE: {(results.tracking_error.tracking_error * 100).toFixed(2)}%
                    </span>
                  )}
                </h5>
              </div>
              <div className="card-body">
                <div style={{ height: `${Math.max(tiltsChartData.labels.length * 28, 180)}px` }}>
                  <Bar data={tiltsChartData} options={tiltsChartOptions} />
                </div>
                <div className="tilts-legend mt-2">
                  <span className="tilts-legend-item">
                    <span className="tilts-legend-dot tilts-legend-ow"></span> Overweight
                  </span>
                  <span className="tilts-legend-item">
                    <span className="tilts-legend-dot tilts-legend-uw"></span> Underweight
                  </span>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Right Column: Charts */}
        <div className="col-lg-8">

          {/* Tabs */}
          {results && (
            <ul className="nav nav-tabs mb-3" style={{ borderBottom: '2px solid #072040' }}>
              <li className="nav-item">
                <button
                  className={`nav-link${activeTab === 'analysis' ? ' active' : ''}`}
                  onClick={() => setActiveTab('analysis')}
                  style={activeTab === 'analysis' ? { color: '#072040', fontWeight: 600, borderColor: '#072040 #072040 #fff' } : { color: '#6c757d' }}
                >
                  Risk Analysis
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link${activeTab === 'factors' ? ' active' : ''}`}
                  onClick={() => setActiveTab('factors')}
                  style={activeTab === 'factors' ? { color: '#072040', fontWeight: 600, borderColor: '#072040 #072040 #fff' } : { color: '#6c757d' }}
                >
                  Factor Exposures
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link${activeTab === 'attribution' ? ' active' : ''}`}
                  onClick={() => setActiveTab('attribution')}
                  style={activeTab === 'attribution' ? { color: '#072040', fontWeight: 600, borderColor: '#072040 #072040 #fff' } : { color: '#6c757d' }}
                >
                  Factor Attribution
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link${activeTab === 'te' ? ' active' : ''}`}
                  onClick={() => setActiveTab('te')}
                  style={activeTab === 'te' ? { color: '#072040', fontWeight: 600, borderColor: '#072040 #072040 #fff' } : { color: '#6c757d' }}
                >
                  Tracking Error
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link${activeTab === 'builder' ? ' active' : ''}`}
                  onClick={() => setActiveTab('builder')}
                  style={activeTab === 'builder' ? { color: '#072040', fontWeight: 600, borderColor: '#072040 #072040 #fff' } : { color: '#6c757d' }}
                >
                  Portfolio Builder
                </button>
              </li>
            </ul>
          )}

          {/* ── Risk Analysis Tab ── */}
          {activeTab === 'analysis' && (
            <>
          {/* Portfolio Scenario Metrics */}
          {results?.stochastics?.scenario_metrics && (() => {
            const PERCENTILE_OPTIONS = [
              { key: 'p5',  label: 'p5  — Very Adverse' },
              { key: 'p10', label: 'p10 — Adverse' },
              { key: 'p25', label: 'p25 — Below Median' },
              { key: 'p50', label: 'p50 — Median' },
              { key: 'p75', label: 'p75 — Above Median' },
              { key: 'p90', label: 'p90 — Favourable' },
              { key: 'p95', label: 'p95 — Very Favourable' },
            ];

            const sm = results.stochastics.scenario_metrics[selectedPercentile];

            const fmt = (v, type) => {
              if (v == null) return '—';
              if (type === 'pct') return `${(v * 100).toFixed(2)}%`;
              if (type === 'ratio') return v.toFixed(2);
              return v;
            };

            const isGood = (key, v) => {
              const goodHigh = ['annualised_return', 'arithmetic_return', 'return_risk_ratio', 'sharpe', 'win_rate'];
              const goodLow  = ['max_drawdown', 'avg_drawdown', 'var', 'cvar'];
              if (goodHigh.includes(key)) return v >= 0;
              if (goodLow.includes(key))  return v >= -0.05;
              return null;
            };

            const rows = [
              { key: 'annualised_return',  label: 'Annualised Return (Geometric)', type: 'pct',   desc: 'Compounded geometric mean return annualised over the simulation horizon' },
              { key: 'arithmetic_return', label: 'Annualised Return (Arithmetic)', type: 'pct',   desc: 'Simple average of annual returns across the simulation path' },
              { key: 'volatility',         label: 'Annualised Volatility',    type: 'pct',   desc: 'Realised annualised std deviation of annual log returns' },
              { key: 'return_risk_ratio',  label: 'Return / Risk Ratio',      type: 'ratio', desc: 'Annualised return divided by annualised volatility' },
              { key: 'sharpe',             label: 'Sharpe Ratio (rf=3%)',     type: 'ratio', desc: 'Risk-adjusted return above the 3% risk-free rate' },
              { key: 'max_drawdown',       label: 'Maximum Drawdown',         type: 'pct',   desc: 'Worst peak-to-trough decline within the simulation path' },
              { key: 'avg_drawdown',       label: 'Average Drawdown',         type: 'pct',   desc: 'Mean drawdown across all time steps in the simulation path' },
              { key: 'var',                label: '1-Year VaR',               type: 'pct',   desc: 'Value at Risk — worst 1-year return at this percentile' },
              { key: 'cvar',               label: '1-Year CVaR',              type: 'pct',   desc: 'Conditional VaR — average loss in the tail beyond VaR' },
              { key: 'win_rate',           label: 'Win Rate',                 type: 'pct',   desc: '% of simulation years with a positive return' },
            ];

            return (
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 style={{ margin: 0 }}>Portfolio Scenario Metrics</h5>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto', minWidth: '200px', fontSize: '0.82rem' }}
                    value={selectedPercentile}
                    onChange={(e) => setSelectedPercentile(e.target.value)}
                  >
                    {PERCENTILE_OPTIONS.map(o => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm mb-0" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ padding: '0.5rem 0.75rem', color: '#072040', fontWeight: 600, width: '40%' }}>Metric</th>
                        <th style={{ padding: '0.5rem 0.75rem', color: '#072040', fontWeight: 600, textAlign: 'right', width: '15%' }}>Value</th>
                        <th style={{ padding: '0.5rem 0.75rem', color: '#6c757d', fontWeight: 400, width: '45%', fontSize: '0.78rem' }}>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => {
                        const val = sm?.[row.key];
                        const good = isGood(row.key, val);
                        const valueColor = good === true ? '#2d7a3a' : good === false ? '#c0392b' : '#212529';
                        return (
                          <tr key={row.key} style={{ borderBottom: '1px solid #E7E6E6', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '0.45rem 0.75rem', fontWeight: 500, color: '#212529' }}>{row.label}</td>
                            <td style={{ padding: '0.45rem 0.75rem', textAlign: 'right', fontWeight: 600, color: valueColor, fontVariantNumeric: 'tabular-nums' }}>
                              {fmt(val, row.type)}
                            </td>
                            <td style={{ padding: '0.45rem 0.75rem', color: '#6c757d', fontSize: '0.78rem' }}>{row.desc}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Contribution to Portfolio Volatility */}
          {results?.vol_contributions?.length > 0 && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 style={{ margin: 0 }}>Contribution to Portfolio Volatility</h5>
              </div>
              <div className="card-body p-0">
                <table className="table table-sm mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.5rem 0.75rem', color: '#072040', fontWeight: 600 }}>Asset Class</th>
                      <th style={{ padding: '0.5rem 0.75rem', color: '#072040', fontWeight: 600, textAlign: 'right' }}>Weight</th>
                      <th style={{ padding: '0.5rem 0.75rem', color: '#072040', fontWeight: 600, textAlign: 'right' }}>Vol Contribution</th>
                      <th style={{ padding: '0.5rem 0.75rem', color: '#072040', fontWeight: 600, textAlign: 'right' }}>% of Total Vol</th>
                      <th style={{ padding: '0.5rem 0.75rem', width: '25%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.vol_contributions.map((row, i) => {
                      const barWidth = Math.max(0, Math.min(100, Math.abs(row.contribution_pct)));
                      const isPos = row.contribution >= 0;
                      return (
                        <tr key={row.asset_class} style={{ borderBottom: '1px solid #E7E6E6', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '0.4rem 0.75rem', fontWeight: 500 }}>{getDisplayName(row.asset_class)}</td>
                          <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', color: '#6c757d' }}>{(row.weight * 100).toFixed(1)}%</td>
                          <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontWeight: 600, color: isPos ? '#072040' : '#c0392b', fontVariantNumeric: 'tabular-nums' }}>
                            {(row.contribution * 100).toFixed(2)}%
                          </td>
                          <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', color: '#6c757d', fontVariantNumeric: 'tabular-nums' }}>
                            {row.contribution_pct.toFixed(1)}%
                          </td>
                          <td style={{ padding: '0.4rem 0.75rem' }}>
                            <div style={{ background: '#E7E6E6', borderRadius: 3, height: 10, overflow: 'hidden' }}>
                              <div style={{ width: `${barWidth}%`, height: '100%', backgroundColor: isPos ? '#599BE3' : '#ffc107', borderRadius: 3 }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    <tr style={{ borderTop: '2px solid #072040', backgroundColor: '#f0f4f8' }}>
                      <td style={{ padding: '0.4rem 0.75rem', fontWeight: 700 }}>Total</td>
                      <td style={{ padding: '0.4rem 0.75rem' }}></td>
                      <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {(results.vol_contributions.reduce((s, r) => s + r.contribution, 0) * 100).toFixed(2)}%
                      </td>
                      <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontWeight: 700 }}>100.0%</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Contribution to Tracking Error */}
          {results?.tracking_error?.te_contributions?.length > 0 && (
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 style={{ margin: 0 }}>Contribution to Tracking Error</h5>
                <span style={{ fontSize: '0.82rem', color: '#6c757d' }}>
                  Total TE: <strong>{(results.tracking_error.tracking_error * 100).toFixed(2)}%</strong>
                </span>
              </div>
              <div className="card-body p-0">
                <table className="table table-sm mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.5rem 0.75rem', color: '#072040', fontWeight: 600 }}>Asset Class</th>
                      <th style={{ padding: '0.5rem 0.75rem', color: '#072040', fontWeight: 600, textAlign: 'right' }}>Tilt (pp)</th>
                      <th style={{ padding: '0.5rem 0.75rem', color: '#072040', fontWeight: 600, textAlign: 'right' }}>TE Contribution</th>
                      <th style={{ padding: '0.5rem 0.75rem', color: '#072040', fontWeight: 600, textAlign: 'right' }}>% of Total TE</th>
                      <th style={{ padding: '0.5rem 0.75rem', width: '25%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.tracking_error.te_contributions.map((row, i) => {
                      const barWidth = Math.max(0, Math.min(100, Math.abs(row.contribution_pct)));
                      const isPos = row.tilt >= 0;
                      return (
                        <tr key={row.asset_class} style={{ borderBottom: '1px solid #E7E6E6', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '0.4rem 0.75rem', fontWeight: 500 }}>{getDisplayName(row.asset_class)}</td>
                          <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontWeight: 600, color: isPos ? '#599BE3' : '#f0a500', fontVariantNumeric: 'tabular-nums' }}>
                            {row.tilt >= 0 ? '+' : ''}{row.tilt.toFixed(2)}pp
                          </td>
                          <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontWeight: 600, color: '#072040', fontVariantNumeric: 'tabular-nums' }}>
                            {(row.contribution * 100).toFixed(2)}%
                          </td>
                          <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', color: '#6c757d', fontVariantNumeric: 'tabular-nums' }}>
                            {row.contribution_pct.toFixed(1)}%
                          </td>
                          <td style={{ padding: '0.4rem 0.75rem' }}>
                            <div style={{ background: '#E7E6E6', borderRadius: 3, height: 10, overflow: 'hidden' }}>
                              <div style={{ width: `${barWidth}%`, height: '100%', backgroundColor: '#599BE3', borderRadius: 3 }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TE Through Time */}
          {results?.te_through_time?.dates?.length > 0 && (() => {
            const { dates, series, te_series } = results.te_through_time;

            const datasets = Object.entries(series).map(([sac, values]) => ({
              label: getDisplayName(sac),
              data: values.map(v => parseFloat((v * 100).toFixed(4))),
              backgroundColor: SAC_COLORS[sac] || '#aaa',
              borderColor: SAC_COLORS[sac] || '#aaa',
              borderWidth: 0,
              stack: 'contributions',
              type: 'bar',
            }));

            // Total TE overlay line
            datasets.push({
              label: 'Total TE',
              data: te_series.map(v => parseFloat((v * 100).toFixed(4))),
              type: 'line',
              borderColor: '#212529',
              borderWidth: 2,
              borderDash: [4, 3],
              pointRadius: 0,
              fill: false,
              stack: undefined,
              order: -1,
            });

            const chartData = { labels: dates, datasets };

            const chartOptions = {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: { font: { size: 10 }, boxWidth: 12, padding: 8 },
                },
                tooltip: {
                  callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y >= 0 ? '+' : ''}${ctx.parsed.y.toFixed(2)}%`,
                  },
                },
              },
              scales: {
                x: {
                  stacked: true,
                  ticks: { font: { size: 10 }, maxRotation: 45 },
                  grid: { display: false },
                },
                y: {
                  stacked: true,
                  ticks: {
                    font: { size: 10 },
                    callback: (v) => `${v.toFixed(2)}%`,
                  },
                  grid: { color: 'rgba(0,0,0,0.06)' },
                  title: { display: true, text: 'TE Contribution (%)', font: { size: 11 } },
                },
              },
            };

            return (
              <div className="card mb-4">
                <div className="card-header">
                  <h5 style={{ margin: 0 }}>Tracking Error Contribution Through Time</h5>
                </div>
                <div className="card-body" style={{ height: '420px' }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            );
          })()}

          {/* Stress Testing */}
          {results?.stress_test_results && (
            <StressTest stressTestResults={results.stress_test_results} />
          )}
            </>
          )}

          {/* ── Factor Attribution Tab ── */}
          {activeTab === 'attribution' && results?.factor_attribution && (() => {
            // Transform monthly series based on selected window
            const applyWindow = (monthly) => {
              if (attribWindow === 'cumulative') {
                return monthly.reduce((acc, v, i) => {
                  acc.push(parseFloat(((i === 0 ? 0 : acc[i - 1]) + v).toFixed(4)));
                  return acc;
                }, []);
              }
              if (attribWindow === 'monthly') return monthly;
              const w = parseInt(attribWindow, 10);
              return monthly.map((_, i) => {
                if (i < w - 1) return null; // not enough history yet
                const slice = monthly.slice(i - w + 1, i + 1);
                return parseFloat(slice.reduce((a, b) => a + b, 0).toFixed(4));
              });
            };

            const windowLabel = {
              monthly: 'Monthly',
              cumulative: 'Cumulative',
              '3': '3-Month Rolling',
              '6': '6-Month Rolling',
              '12': '12-Month Rolling',
            }[attribWindow] || 'Monthly';

            const buildAttribChart = (sacData, sacLabel) => {
              if (!sacData?.dates?.length) return null;
              const { dates, funds, factors, portfolio_total } = sacData;
              const seriesMap = attribView === 'factors' ? (factors || {}) : funds;
              const seriesNames = Object.keys(seriesMap).filter(
                (name) => seriesMap[name].some((v) => v !== 0)
              );

              const datasets = seriesNames.map((name, i) => ({
                type: 'bar',
                label: name,
                data: applyWindow(seriesMap[name]),
                backgroundColor: FUND_PALETTE[i % FUND_PALETTE.length] + 'CC',
                borderColor: FUND_PALETTE[i % FUND_PALETTE.length],
                borderWidth: 0,
                stack: 'main',
              }));

              // Portfolio total overlay: always cumulative so it shows overall trajectory
              const rawTotal = portfolio_total;
              const cumulativeTotal = rawTotal.reduce((acc, v, i) => {
                acc.push(parseFloat(((i === 0 ? 0 : acc[i - 1]) + v).toFixed(4)));
                return acc;
              }, []);

              datasets.push({
                type: 'line',
                label: 'Portfolio Total (Cumulative)',
                data: cumulativeTotal,
                borderColor: '#212529',
                backgroundColor: 'transparent',
                borderWidth: 2,
                borderDash: [4, 3],
                pointRadius: 0,
                tension: 0.3,
                order: -1,
                yAxisID: attribWindow === 'cumulative' ? 'y' : 'y2',
              });

              const isSingleAxis = attribWindow === 'cumulative';
              const scales = {
                x: {
                  stacked: true,
                  ticks: { font: { size: 10 }, maxRotation: 45 },
                  grid: { display: false },
                },
                y: {
                  stacked: true,
                  ticks: { font: { size: 10 }, callback: (v) => `${v.toFixed(1)}%` },
                  grid: { color: 'rgba(0,0,0,0.06)' },
                  title: { display: true, text: `${windowLabel} Contribution (%)`, font: { size: 10 } },
                },
              };
              if (!isSingleAxis) {
                scales.y2 = {
                  position: 'right',
                  ticks: { font: { size: 10 }, callback: (v) => `${v.toFixed(1)}%` },
                  grid: { display: false },
                  title: { display: true, text: 'Cumulative Total (%)', font: { size: 10 } },
                };
              }

              const options = {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom', labels: { font: { size: 10 }, boxWidth: 12, padding: 8 } },
                  tooltip: {
                    mode: 'nearest',
                    intersect: true,
                    callbacks: {
                      label: (ctx) => {
                        const v = ctx.parsed.y;
                        if (v === null || v === undefined) return null;
                        return `${ctx.dataset.label}: ${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
                      },
                    },
                  },
                },
                scales,
              };

              const viewLabel = attribView === 'factors' ? 'by Factor' : 'by Fund';
              return (
                <div className="card mb-4" key={sacLabel}>
                  <div className="card-header">
                    <h5 style={{ margin: 0 }}>{sacLabel} — Factor Attribution {viewLabel} ({windowLabel})</h5>
                  </div>
                  <div className="card-body" style={{ height: '400px' }}>
                    <Bar data={{ labels: dates, datasets }} options={options} />
                  </div>
                </div>
              );
            };

            return (
              <>
                <div className="d-flex align-items-center mb-3" style={{ gap: '16px', flexWrap: 'wrap' }}>
                  <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>View by:</span>
                    <div className="btn-group btn-group-sm">
                      <button
                        className={`btn ${attribView === 'funds' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setAttribView('funds')}
                      >Fund</button>
                      <button
                        className={`btn ${attribView === 'factors' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setAttribView('factors')}
                      >Factor</button>
                    </div>
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>Window:</span>
                    <div className="btn-group btn-group-sm">
                      {[
                        { key: 'monthly', label: 'Monthly' },
                        { key: 'cumulative', label: 'Cumulative' },
                        { key: '3', label: '3M Rolling' },
                        { key: '6', label: '6M Rolling' },
                        { key: '12', label: '12M Rolling' },
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          className={`btn ${attribWindow === key ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setAttribWindow(key)}
                        >{label}</button>
                      ))}
                    </div>
                  </div>
                </div>
                {buildAttribChart(results.factor_attribution.EQ_AU, 'EQ_AU')}
                {buildAttribChart(results.factor_attribution.EQ_INTL, 'EQ_INTL')}

                {/* ── Fund drill-down ── */}
                {(() => {
                  const fa = results.factor_attribution;
                  // Collect all funds from both SACs with their fund_factors data
                  const allFunds = [
                    ...Object.keys(fa.EQ_AU?.fund_factors || {}).map(n => ({ name: n, sac: 'EQ_AU' })),
                    ...Object.keys(fa.EQ_INTL?.fund_factors || {}).map(n => ({ name: n, sac: 'EQ_INTL' })),
                  ].sort((a, b) => a.name.localeCompare(b.name));

                  if (!allFunds.length) return null;

                  const selectedFund = drillFund || allFunds[0].name;
                  const fundEntry = allFunds.find(f => f.name === selectedFund) || allFunds[0];
                  const sacData = fa[fundEntry.sac];
                  const factorData = sacData?.fund_factors?.[fundEntry.name] || {};
                  const dates = sacData?.dates || [];

                  const factorNames = Object.keys(factorData).filter(
                    f => factorData[f].some(v => v !== 0)
                  );

                  const drillDatasets = factorNames.map((fname, i) => ({
                    type: 'bar',
                    label: fname,
                    data: applyWindow(factorData[fname]),
                    backgroundColor: FUND_PALETTE[i % FUND_PALETTE.length] + 'CC',
                    borderColor: FUND_PALETTE[i % FUND_PALETTE.length],
                    borderWidth: 0,
                    stack: 'main',
                  }));

                  // Fund total line (cumulative)
                  const fundMonthly = sacData?.funds?.[fundEntry.name] || [];
                  const fundCumTotal = fundMonthly.reduce((acc, v, i) => {
                    acc.push(parseFloat(((i === 0 ? 0 : acc[i - 1]) + v).toFixed(4)));
                    return acc;
                  }, []);
                  drillDatasets.push({
                    type: 'line',
                    label: 'Fund Total (Cumulative)',
                    data: fundCumTotal,
                    borderColor: '#072040',
                    backgroundColor: 'transparent',
                    borderWidth: 2,
                    borderDash: [4, 3],
                    pointRadius: 0,
                    tension: 0.3,
                    order: -1,
                    yAxisID: attribWindow === 'cumulative' ? 'y' : 'y2',
                  });

                  const isSingleAxis = attribWindow === 'cumulative';
                  const drillScales = {
                    x: {
                      stacked: true,
                      ticks: { font: { size: 10 }, maxRotation: 45 },
                      grid: { display: false },
                    },
                    y: {
                      stacked: true,
                      ticks: { font: { size: 10 }, callback: v => `${v.toFixed(1)}%` },
                      grid: { color: 'rgba(0,0,0,0.06)' },
                      title: { display: true, text: `${windowLabel} Contribution (%)`, font: { size: 10 } },
                    },
                  };
                  if (!isSingleAxis) {
                    drillScales.y2 = {
                      position: 'right',
                      ticks: { font: { size: 10 }, callback: v => `${v.toFixed(1)}%` },
                      grid: { display: false },
                      title: { display: true, text: 'Cumulative Total (%)', font: { size: 10 } },
                    };
                  }

                  return (
                    <div className="card mb-4">
                      <div className="card-header d-flex align-items-center justify-content-between">
                        <h5 style={{ margin: 0 }}>Fund Factor Drill-Down</h5>
                        <select
                          className="form-select form-select-sm"
                          style={{ width: 'auto', minWidth: '260px' }}
                          value={selectedFund}
                          onChange={e => setDrillFund(e.target.value)}
                        >
                          {['EQ_AU', 'EQ_INTL'].map(sac => {
                            const sacFunds = allFunds.filter(f => f.sac === sac);
                            if (!sacFunds.length) return null;
                            return (
                              <optgroup key={sac} label={sac}>
                                {sacFunds.map(f => (
                                  <option key={f.name} value={f.name}>{f.name}</option>
                                ))}
                              </optgroup>
                            );
                          })}
                        </select>
                      </div>
                      <div className="card-body" style={{ height: '380px' }}>
                        {factorNames.length > 0 ? (
                          <Bar
                            data={{ labels: dates, datasets: drillDatasets }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { position: 'bottom', labels: { font: { size: 10 }, boxWidth: 12, padding: 8 } },
                                tooltip: {
                                  mode: 'nearest',
                                  intersect: true,
                                  callbacks: {
                                    label: ctx => {
                                      const v = ctx.parsed.y;
                                      if (v === null || v === undefined) return null;
                                      return `${ctx.dataset.label}: ${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
                                    },
                                  },
                                },
                              },
                              scales: drillScales,
                            }}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                            No factor data available for this fund.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </>
            );
          })()}

          {activeTab === 'factors' && results?.factor_exposures && (
            <div className="card mb-4">
              <div className="card-header">
                <h5>Factor Exposures</h5>
              </div>
              <div className="card-body p-0">
                {Object.entries(results.factor_exposures.sac_groups).map(([sac, funds]) => {
                  const factors = results.factor_exposures.sac_factors[sac] || [];

                  // Portfolio total row: weighted averages across all funds in this SAC
                  const totalWeight = funds.reduce((s, f) => s + (f.weight ?? 0), 0);
                  const wtAvg = (getter) => {
                    if (totalWeight === 0) return null;
                    const sum = funds.reduce((s, f) => {
                      const v = getter(f);
                      return v != null ? s + (f.weight ?? 0) * v : s;
                    }, 0);
                    const wSum = funds.reduce((s, f) => getter(f) != null ? s + (f.weight ?? 0) : s, 0);
                    return wSum > 0 ? sum / wSum : null;
                  };
                  const totalR2   = wtAvg((f) => f.r_squared);
                  const totalSpec = wtAvg((f) => f.specific_risk_annual);
                  const totalFac  = wtAvg((f) => f.factor_risk_annual);
                  const totalRisk = wtAvg((f) => f.total_risk_annual);
                  const totalBetas = Object.fromEntries(
                    factors.map((factor) => [factor, wtAvg((f) => f.betas?.[factor] ?? null)])
                  );

                  return (
                    <div key={sac} className="factor-sac-section">
                      <div className="factor-sac-header">{sac}</div>
                      <div className="table-responsive">
                        <table className="table table-sm mb-0 factor-table">
                          <thead>
                            <tr>
                              <th className="factor-th-fund">Fund</th>
                              <th className="factor-th-num" title="Portfolio weight">Wt %</th>
                              <th className="factor-th-num" title="Model fit (R-squared)">R²</th>
                              {factors.map((f) => (
                                <th key={f} className="factor-th-beta" title={f}>
                                  {abbreviateFactorName(f)}
                                </th>
                              ))}
                              <th className="factor-th-num" title="Specific (idiosyncratic) risk">Spec</th>
                              <th className="factor-th-num" title="Systematic factor risk">Factor</th>
                              <th className="factor-th-num" title="Total risk (annualised)">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {funds.map((fund) => (
                              <tr key={fund.tr_code}>
                                <td className="factor-fund-name" title={fund.fund_name}>
                                  {fund.fund_name.length > 30
                                    ? fund.fund_name.substring(0, 28) + '...'
                                    : fund.fund_name}
                                </td>
                                <td className="factor-cell-num">
                                  {fund.weight != null ? fund.weight.toFixed(1) : '-'}
                                </td>
                                <td className="factor-cell-num">
                                  {fund.r_squared != null ? fund.r_squared.toFixed(3) : '-'}
                                </td>
                                {factors.map((f) => {
                                  const beta = fund.betas?.[f];
                                  return (
                                    <td
                                      key={f}
                                      className="factor-beta-cell"
                                      style={{ backgroundColor: getBetaColor(beta) }}
                                    >
                                      {beta != null ? beta.toFixed(2) : '-'}
                                    </td>
                                  );
                                })}
                                <td className="factor-cell-num">
                                  {fund.specific_risk_annual != null
                                    ? (fund.specific_risk_annual * 100).toFixed(1) + '%'
                                    : '-'}
                                </td>
                                <td className="factor-cell-num">
                                  {fund.factor_risk_annual != null
                                    ? (fund.factor_risk_annual * 100).toFixed(1) + '%'
                                    : '-'}
                                </td>
                                <td className="factor-cell-num">
                                  {fund.total_risk_annual != null
                                    ? (fund.total_risk_annual * 100).toFixed(1) + '%'
                                    : '-'}
                                </td>
                              </tr>
                            ))}
                            {/* Portfolio total row */}
                            <tr style={{ borderTop: '2px solid #072040', backgroundColor: '#f0f4f8', fontWeight: 600 }}>
                              <td className="factor-fund-name" style={{ color: '#072040' }}>Portfolio Total</td>
                              <td className="factor-cell-num" style={{ color: '#072040' }}>
                                {totalWeight.toFixed(1)}
                              </td>
                              <td className="factor-cell-num" style={{ color: '#072040' }}>
                                {totalR2 != null ? totalR2.toFixed(3) : '-'}
                              </td>
                              {factors.map((f) => {
                                const beta = totalBetas[f];
                                return (
                                  <td
                                    key={f}
                                    className="factor-beta-cell"
                                    style={{ backgroundColor: getBetaColor(beta), fontWeight: 600 }}
                                  >
                                    {beta != null ? beta.toFixed(2) : '-'}
                                  </td>
                                );
                              })}
                              <td className="factor-cell-num" style={{ color: '#072040' }}>
                                {totalSpec != null ? (totalSpec * 100).toFixed(1) + '%' : '-'}
                              </td>
                              <td className="factor-cell-num" style={{ color: '#072040' }}>
                                {totalFac != null ? (totalFac * 100).toFixed(1) + '%' : '-'}
                              </td>
                              <td className="factor-cell-num" style={{ color: '#072040' }}>
                                {totalRisk != null ? (totalRisk * 100).toFixed(1) + '%' : '-'}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'factors' && !results?.factor_exposures && results && (
            <div className="text-muted p-3">No factor exposure data available for this portfolio.</div>
          )}

          {/* ── Factor Exposure Through Time ── */}
          {activeTab === 'factors' && results?.factor_exposure_history && (() => {
            const buildExpHistChart = (sacData, sacLabel) => {
              if (!sacData?.dates?.length) return null;
              const { dates, factors, fund_exposures, portfolio_exposure } = sacData;
              const selectedFactor = expHistFactor[sacLabel] || factors[0] || '';

              let datasets = [];

              if (expHistView === 'factor') {
                // Stacked bars: one bar segment per factor showing portfolio total beta
                datasets = factors.map((f, i) => ({
                  type: 'bar',
                  label: f,
                  data: portfolio_exposure[f] || [],
                  backgroundColor: FUND_PALETTE[i % FUND_PALETTE.length] + 'CC',
                  borderColor: FUND_PALETTE[i % FUND_PALETTE.length],
                  borderWidth: 0,
                  stack: 'main',
                }));
              } else {
                // Stacked bars: one segment per fund's contribution to selected factor
                const fundNames = Object.keys(fund_exposures).filter(
                  (fname) => fund_exposures[fname][selectedFactor]?.some((v) => v !== 0)
                );
                datasets = fundNames.map((fname, i) => ({
                  type: 'bar',
                  label: fname,
                  data: fund_exposures[fname][selectedFactor] || [],
                  backgroundColor: FUND_PALETTE[i % FUND_PALETTE.length] + 'CC',
                  borderColor: FUND_PALETTE[i % FUND_PALETTE.length],
                  borderWidth: 0,
                  stack: 'main',
                }));
                // Dashed overlay: portfolio total
                if (portfolio_exposure[selectedFactor]) {
                  datasets.push({
                    type: 'line',
                    label: 'Portfolio Total',
                    data: portfolio_exposure[selectedFactor],
                    borderColor: '#212529',
                    backgroundColor: 'transparent',
                    borderWidth: 2,
                    borderDash: [6, 3],
                    pointRadius: 0,
                    tension: 0.3,
                    order: -1,
                  });
                }
              }

              const chartData = { labels: dates, datasets };
              const chartOptions = {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'nearest', intersect: true },
                plugins: {
                  legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
                  tooltip: {
                    mode: 'nearest',
                    intersect: true,
                    callbacks: {
                      title: () => '',
                      label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(3) ?? '-'}`,
                    },
                  },
                },
                scales: {
                  x: { stacked: true, ticks: { maxTicksLimit: 12, font: { size: 10 } } },
                  y: {
                    stacked: true,
                    title: { display: true, text: 'Portfolio-Weighted Beta' },
                    ticks: { font: { size: 10 } },
                  },
                },
              };

              return (
                <div className="card mb-4" key={sacLabel}>
                  <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <h6 className="mb-0">{sacLabel} — Factor Exposure Through Time</h6>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {/* View toggle */}
                      <div className="btn-group btn-group-sm">
                        <button
                          className={`btn ${expHistView === 'factor' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setExpHistView('factor')}
                          style={{ fontSize: '0.75rem' }}
                        >By Factor</button>
                        <button
                          className={`btn ${expHistView === 'fund' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setExpHistView('fund')}
                          style={{ fontSize: '0.75rem' }}
                        >By Fund</button>
                      </div>
                      {/* Factor selector (By Fund only) */}
                      {expHistView === 'fund' && (
                        <select
                          className="form-select form-select-sm"
                          style={{ width: 'auto', fontSize: '0.75rem' }}
                          value={selectedFactor}
                          onChange={(e) => setExpHistFactor((prev) => ({ ...prev, [sacLabel]: e.target.value }))}
                        >
                          {factors.map((f) => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  <div className="card-body" style={{ height: 320 }}>
                    <Bar data={chartData} options={chartOptions} />
                  </div>
                </div>
              );
            };

            const feh = results.factor_exposure_history;
            return (
              <>
                {buildExpHistChart(feh.EQ_AU, 'EQ_AU')}
                {buildExpHistChart(feh.EQ_INTL, 'EQ_INTL')}
              </>
            );
          })()}

          {/* ── Factor Correlation Matrices ── */}
          {activeTab === 'factors' && results?.factor_correlations && (() => {
            const sacLabels = { EQ_AU: 'AU Equity', EQ_INTL: 'International Equity' };
            const sacs = ['EQ_AU', 'EQ_INTL'].filter(s => results.factor_correlations[s]);
            if (!sacs.length) return null;
            return (
              <div className="card mb-4">
                <div className="card-header">
                  <h5>Factor Correlations</h5>
                </div>
                <div className="card-body p-0">
                  {sacs.map(sac => {
                    const { factors, matrix } = results.factor_correlations[sac];
                    return (
                      <div key={sac} className="factor-sac-section">
                        <div className="factor-sac-header">{sacLabels[sac] || sac}</div>
                        <div className="table-responsive">
                          <table className="table table-sm mb-0 factor-table">
                            <thead>
                              <tr>
                                <th className="factor-th-fund" style={{ minWidth: 100 }}></th>
                                {factors.map(f => (
                                  <th key={f} className="factor-th-beta" title={f}>
                                    {abbreviateFactorName(f)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {factors.map((rowF, ri) => (
                                <tr key={rowF}>
                                  <td className="factor-fund-name" style={{ fontSize: '0.72rem', fontWeight: 600, color: '#072040' }}>
                                    {abbreviateFactorName(rowF)}
                                  </td>
                                  {factors.map((colF, ci) => {
                                    const val = matrix[ri][ci];
                                    const isDiag = ri === ci;
                                    return (
                                      <td
                                        key={colF}
                                        className="factor-beta-cell"
                                        style={{
                                          backgroundColor: isDiag ? '#f0f4f8' : getCorrelationColor(val),
                                          fontWeight: isDiag ? 700 : 500,
                                          color: isDiag ? '#072040' : undefined,
                                        }}
                                      >
                                        {val != null ? val.toFixed(2) : '-'}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── Tracking Error Tab ── */}
          {activeTab === 'te' && results?.equity_tracking_error && (() => {
            const ete = results.equity_tracking_error;
            const sacs = [
              { key: 'EQ_AU', label: 'EQ_AU', color: '#072040' },
              { key: 'EQ_INTL', label: 'EQ_INTL', color: '#599BE3' },
            ].filter(s => ete[s.key]);

            // ── Section 1: Summary stat cards ──
            const summaryCards = (
              <div className="row mb-4">
                {sacs.map(({ key, label, color }) => (
                  <div className="col-md-6 mb-3" key={key}>
                    <div className="card h-100" style={{ borderTop: `4px solid ${color}` }}>
                      <div className="card-body text-center">
                        <div className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>{label} — Current Tracking Error</div>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color }}>
                          {ete[key].current_te.toFixed(2)}%
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>Annualised, based on fund alpha covariance</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );

            // ── Section 2: Rolling 12m TE line chart (both SACs) ──
            const allDates = sacs.reduce((acc, { key }) => {
              const d = ete[key].dates;
              return d.length > acc.length ? d : acc;
            }, []);

            const rollingTeChart = (() => {
              // Build unified date labels from longest series
              const labels = allDates;
              const alignedDatasets = sacs.map(({ key, label, color }) => {
                const dateMap = {};
                ete[key].dates.forEach((d, i) => { dateMap[d] = ete[key].rolling_te[i]; });
                return {
                  label,
                  data: labels.map(d => dateMap[d] ?? null),
                  borderColor: color,
                  backgroundColor: color + '33',
                  borderWidth: 2,
                  pointRadius: 0,
                  tension: 0.3,
                  spanGaps: false,
                };
              });

              return (
                <div className="card mb-4">
                  <div className="card-header"><h6 className="mb-0">Rolling 12-Month Tracking Error</h6></div>
                  <div className="card-body" style={{ height: 300 }}>
                    <Line
                      data={{ labels, datasets: alignedDatasets }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'nearest', intersect: true },
                        plugins: {
                          legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
                          tooltip: {
                            mode: 'nearest',
                            intersect: true,
                            callbacks: {
                              title: () => '',
                              label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y != null ? ctx.parsed.y.toFixed(2) + '%' : '-'}`,
                            },
                          },
                        },
                        scales: {
                          x: { ticks: { maxTicksLimit: 12, font: { size: 10 } } },
                          y: {
                            title: { display: true, text: 'TE (%)' },
                            ticks: { font: { size: 10 }, callback: v => v.toFixed(1) + '%' },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              );
            })();

            // ── Section 3: Fund TE contribution — stacked bar through time + total line ──
            const contributionCharts = sacs.map(({ key, label }) => {
              const rollingContribs = ete[key].rolling_fund_contributions || {};
              const contribDates = Object.keys(rollingContribs).sort();
              if (!contribDates.length) return null;

              // Collect all unique fund names across all dates
              const allFunds = [...new Set(contribDates.flatMap(d => Object.keys(rollingContribs[d])))];

              const barDatasets = allFunds.map((fname, i) => ({
                type: 'bar',
                label: fname,
                data: contribDates.map(d => rollingContribs[d][fname] ?? 0),
                backgroundColor: FUND_PALETTE[i % FUND_PALETTE.length] + 'CC',
                borderColor: FUND_PALETTE[i % FUND_PALETTE.length],
                borderWidth: 0,
                stack: 'main',
              }));

              // Total line — sum of all fund contributions at each date (= rolling TE at those dates)
              const totalData = contribDates.map(d =>
                Object.values(rollingContribs[d]).reduce((s, v) => s + v, 0)
              );
              const totalDataset = {
                type: 'line',
                label: 'Total TE',
                data: totalData,
                borderColor: '#212529',
                backgroundColor: 'transparent',
                borderWidth: 2,
                borderDash: [6, 3],
                pointRadius: 0,
                tension: 0.3,
                order: -1,
              };

              return (
                <div className="card mb-4" key={key}>
                  <div className="card-header">
                    <h6 className="mb-0">{label} — Fund TE Contribution Through Time</h6>
                  </div>
                  <div className="card-body" style={{ height: 460 }}>
                    <Bar
                      data={{ labels: contribDates, datasets: [...barDatasets, totalDataset] }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'nearest', intersect: true },
                        plugins: {
                          legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
                          tooltip: {
                            mode: 'nearest',
                            intersect: true,
                            callbacks: {
                              title: () => '',
                              label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(2) ?? '-'}%`,
                            },
                          },
                        },
                        scales: {
                          x: { stacked: true, ticks: { maxTicksLimit: 12, font: { size: 10 } } },
                          y: {
                            stacked: true,
                            title: { display: true, text: 'TE Contribution (%)' },
                            ticks: { font: { size: 10 }, callback: v => v.toFixed(1) + '%' },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              );
            });

            // ── Section 4: Monthly realised portfolio alpha bar chart (one per SAC) ──
            const alphaCharts = sacs.map(({ key, label }) => {
              const alphaData = ete[key].portfolio_alpha;
              const alphaDates = ete[key].dates;
              const bgColors = alphaData.map(v => v >= 0 ? '#91CF9B' : '#ffc107');
              return (
                <div className="card mb-4" key={key}>
                  <div className="card-header"><h6 className="mb-0">{label} — Monthly Realised Portfolio Alpha</h6></div>
                  <div className="card-body" style={{ height: 260 }}>
                    <Bar
                      data={{
                        labels: alphaDates,
                        datasets: [{
                          label: 'Portfolio Alpha',
                          data: alphaData,
                          backgroundColor: bgColors,
                          borderWidth: 0,
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'nearest', intersect: true },
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            mode: 'nearest',
                            intersect: true,
                            callbacks: {
                              title: ctx => ctx[0]?.label || '',
                              label: ctx => `Alpha: ${ctx.parsed.y.toFixed(2)}%`,
                            },
                          },
                        },
                        scales: {
                          x: { ticks: { maxTicksLimit: 12, font: { size: 10 } } },
                          y: {
                            title: { display: true, text: 'Alpha (%)' },
                            ticks: { font: { size: 10 }, callback: v => v.toFixed(1) + '%' },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              );
            });

            return (
              <>
                {summaryCards}
                {rollingTeChart}
                {contributionCharts}
                {alphaCharts}
              </>
            );
          })()}

          {activeTab === 'te' && !results?.equity_tracking_error && results && (
            <div className="text-muted p-3">No alpha data available for this portfolio's equity holdings.</div>
          )}

          {/* ── Portfolio Builder Tab ── */}
          {activeTab === 'builder' && results && (() => {
            if (!builderHoldings) return <div className="text-muted p-3">Loading...</div>;

            const updateWeight = (sac, idx, val) => {
              setBuilderResults(prev => ({ ...prev, [sac]: null }));
              setBuilderError(null);
              setBuilderHoldings(prev => {
                const updated = [...prev[sac]];
                updated[idx] = { ...updated[idx], weight: val };
                return { ...prev, [sac]: updated };
              });
            };

            const removeFund = (sac, idx) => {
              setBuilderResults(prev => ({ ...prev, [sac]: null }));
              setBuilderError(null);
              setBuilderHoldings(prev => ({
                ...prev,
                [sac]: prev[sac].filter((_, i) => i !== idx),
              }));
            };

            const addFund = (sac) => {
              const tr_code = builderAddSel[sac];
              const weight = parseFloat(builderAddWt[sac]);
              if (!tr_code || isNaN(weight)) return;
              const fund = availableFunds.find(f => f.tr_code === tr_code);
              if (!fund) return;
              setBuilderResults(prev => ({ ...prev, [sac]: null }));
              setBuilderError(null);
              setBuilderHoldings(prev => ({
                ...prev,
                [sac]: [...prev[sac], { tr_code, name: fund.name, weight }],
              }));
              setBuilderAddSel(prev => ({ ...prev, [sac]: '' }));
              setBuilderAddWt(prev => ({ ...prev, [sac]: '' }));
            };

            const handleCalculate = async () => {
              setBuilderLoading(true);
              setBuilderResults(prev => ({ ...prev, [builderSac]: null }));
              setBuilderError(null);
              try {
                // Auto-include any pending fund in the add row
                const pendingCode = builderAddSel[builderSac];
                const pendingWt = parseFloat(builderAddWt[builderSac]);
                const pendingExtra = [];
                if (pendingCode && !isNaN(pendingWt) && pendingWt > 0) {
                  const pf = availableFunds.find(f => f.tr_code === pendingCode);
                  if (pf) {
                    setBuilderHoldings(prev => ({
                      ...prev,
                      [builderSac]: [...prev[builderSac], { tr_code: pendingCode, name: pf.name, weight: pendingWt }],
                    }));
                    setBuilderAddSel(prev => ({ ...prev, [builderSac]: '' }));
                    setBuilderAddWt(prev => ({ ...prev, [builderSac]: '' }));
                    pendingExtra.push({ tr_code: pendingCode, weight: pendingWt / 100, sac: builderSac });
                  }
                }

                const proposed = [
                  ...builderHoldings[builderSac].map(f => ({
                    tr_code: f.tr_code,
                    weight: f.weight / 100,
                    sac: builderSac,
                  })),
                  ...pendingExtra,
                ];
                const res = await api.post('/api/risk-model/portfolio-builder', {
                  portfolio: selectedPortfolio, proposed,
                });
                // res.data is keyed by SAC; extract only the current SAC's result
                const sacResult = res.data?.[builderSac] ?? null;
                setBuilderResults(prev => ({ ...prev, [builderSac]: sacResult }));
              } catch (e) {
                console.error('Portfolio builder error:', e);
                setBuilderError(e?.response?.data?.detail || 'Calculation failed. Check console for details.');
              } finally {
                setBuilderLoading(false);
              }
            };

            const tabStyle = { color: '#072040', fontSize: '0.82rem' };

            // Holdings editor for one SAC
            const renderEditor = (sac) => {
              const holdings = builderHoldings[sac] || [];
              const wtSum = holdings.reduce((s, f) => s + (parseFloat(f.weight) || 0), 0);
              const sumOk = wtSum >= 95 && wtSum <= 105;
              const sacFunds = availableFunds.filter(f => f.sac === sac);
              const heldCodes = new Set(holdings.map(f => f.tr_code));

              return (
                <div className="card h-100" key={sac}>
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">{sac} — Proposed Holdings</h6>
                    <span style={{ fontSize: '0.8rem', color: sumOk ? '#28a745' : '#dc3545', fontWeight: 600 }}>
                      Sum: {wtSum.toFixed(1)}%
                    </span>
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-sm mb-0" style={tabStyle}>
                      <thead style={{ backgroundColor: '#f8f9fa' }}>
                        <tr>
                          <th style={{ width: '55%' }}>Fund</th>
                          <th style={{ width: '25%' }}>Weight (%)</th>
                          <th style={{ width: '10%' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {holdings.map((f, idx) => (
                          <tr key={f.tr_code}>
                            <td>
                              <div style={{ fontWeight: 500, fontSize: '0.78rem' }}>{f.name}</div>
                              <div style={{ color: '#6c757d', fontSize: '0.72rem' }}>{f.tr_code}</div>
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                style={{ width: 80 }}
                                min="0" max="100" step="0.1"
                                value={f.weight}
                                onChange={e => updateWeight(sac, idx, parseFloat(e.target.value) || 0)}
                              />
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                style={{ padding: '1px 6px', fontSize: '0.75rem' }}
                                onClick={() => removeFund(sac, idx)}
                              >×</button>
                            </td>
                          </tr>
                        ))}
                        {/* Add fund row */}
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          <td>
                            <select
                              className="form-select form-select-sm"
                              style={{ fontSize: '0.78rem' }}
                              value={builderAddSel[sac]}
                              onChange={e => setBuilderAddSel(prev => ({ ...prev, [sac]: e.target.value }))}
                            >
                              <option value="">— Add fund —</option>
                              {sacFunds.filter(f => !heldCodes.has(f.tr_code)).map(f => (
                                <option key={f.tr_code} value={f.tr_code}>
                                  {f.name}{!f.has_alpha ? ' (no alpha)' : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: 80 }}
                              min="0" max="100" step="0.1"
                              placeholder="wt %"
                              value={builderAddWt[sac]}
                              onChange={e => setBuilderAddWt(prev => ({ ...prev, [sac]: e.target.value }))}
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-primary"
                              style={{ padding: '1px 8px', fontSize: '0.75rem' }}
                              onClick={() => addFund(sac)}
                              disabled={!builderAddSel[sac] || !builderAddWt[sac]}
                            >+</button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            };

            // Results charts for one SAC
            const renderResults = (sac) => {
              const sacResult = builderResults[sac];
              if (!sacResult) return null;
              const { current, proposed, factors } = sacResult;
              const delta = proposed.te - current.te;

              // TE contribution comparison — grouped bar chart
              // Use full funds list so newly added funds (possibly with 0 TE) still appear
              const allFundNames = [...new Set([
                ...current.funds.map(f => f.name),
                ...proposed.funds.map(f => f.name),
              ])];
              const teChart = allFundNames.length > 0 ? (
                <div className="card mb-3">
                  <div className="card-header"><h6 className="mb-0">{sac} — TE Contribution: Current vs Proposed</h6></div>
                  <div className="card-body" style={{ height: 320 }}>
                    <Bar
                      data={{
                        labels: allFundNames,
                        datasets: [
                          {
                            label: 'Current',
                            data: allFundNames.map(n => current.fund_contributions[n] ?? 0),
                            backgroundColor: '#072040CC',
                            borderColor: '#072040',
                            borderWidth: 0,
                          },
                          {
                            label: 'Proposed',
                            data: allFundNames.map(n => proposed.fund_contributions[n] ?? 0),
                            backgroundColor: '#f0a500CC',
                            borderColor: '#f0a500',
                            borderWidth: 0,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'nearest', intersect: true },
                        plugins: {
                          legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
                          tooltip: {
                            mode: 'nearest', intersect: true,
                            callbacks: { title: () => '', label: ctx => `${ctx.dataset.label} — ${ctx.label}: ${ctx.parsed.y.toFixed(2)}%` },
                          },
                        },
                        scales: {
                          x: { ticks: { font: { size: 9 }, maxRotation: 35 } },
                          y: { title: { display: true, text: 'TE Contribution (%)' }, ticks: { font: { size: 10 }, callback: v => v.toFixed(1) + '%' } },
                        },
                      }}
                    />
                  </div>
                </div>
              ) : null;

              // Factor exposure comparison — grouped bar chart
              const facChart = factors.length > 0 ? (
                <div className="card mb-3">
                  <div className="card-header"><h6 className="mb-0">{sac} — Factor Exposure: Current vs Proposed</h6></div>
                  <div className="card-body" style={{ height: 300 }}>
                    <Bar
                      data={{
                        labels: factors,
                        datasets: [
                          {
                            label: 'Current',
                            data: factors.map(f => current.portfolio_exposure[f] ?? 0),
                            backgroundColor: '#072040CC',
                            borderColor: '#072040',
                            borderWidth: 0,
                          },
                          {
                            label: 'Proposed',
                            data: factors.map(f => proposed.portfolio_exposure[f] ?? 0),
                            backgroundColor: '#f0a500CC',
                            borderColor: '#f0a500',
                            borderWidth: 0,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'nearest', intersect: true },
                        plugins: {
                          legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
                          tooltip: {
                            mode: 'nearest', intersect: true,
                            callbacks: { title: () => '', label: ctx => `${ctx.dataset.label} — ${ctx.label}: ${ctx.parsed.y.toFixed(3)}` },
                          },
                        },
                        scales: {
                          x: { ticks: { font: { size: 9 }, maxRotation: 35 } },
                          y: { title: { display: true, text: 'Portfolio Beta' }, ticks: { font: { size: 10 } } },
                        },
                      }}
                    />
                  </div>
                </div>
              ) : null;

              return (
                <div key={sac}>
                  {/* TE summary */}
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="row text-center">
                        <div className="col-4">
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>Current TE</div>
                          <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#072040' }}>{current.te.toFixed(2)}%</div>
                        </div>
                        <div className="col-4 d-flex align-items-center justify-content-center">
                          <span style={{ fontSize: '1.5rem', color: '#6c757d' }}>→</span>
                        </div>
                        <div className="col-4">
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>Proposed TE</div>
                          <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#072040' }}>{proposed.te.toFixed(2)}%</div>
                          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: delta <= 0 ? '#28a745' : '#dc3545' }}>
                            {delta > 0 ? '+' : ''}{delta.toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {teChart}
                  {facChart}
                </div>
              );
            };

            return (
              <>
                {/* SAC selector + holdings editor */}
                <div className="d-flex align-items-center gap-3 mb-3">
                  <label className="mb-0 fw-semibold" style={{ fontSize: '0.875rem', color: '#072040' }}>Equity Group:</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto', fontSize: '0.875rem' }}
                    value={builderSac}
                    onChange={e => setBuilderSac(e.target.value)}
                  >
                    <option value="EQ_AU">EQ_AU — Australian Equities</option>
                    <option value="EQ_INTL">EQ_INTL — International Equities</option>
                  </select>
                </div>
                <div className="mb-3">{renderEditor(builderSac)}</div>

                {/* Calculate / Reset buttons */}
                <div className="d-flex justify-content-center gap-3 mb-4">
                  <button
                    className="btn btn-primary px-5"
                    onClick={handleCalculate}
                    disabled={builderLoading}
                    style={{ backgroundColor: '#072040', borderColor: '#072040' }}
                  >
                    {builderLoading ? 'Calculating...' : 'Calculate Impact'}
                  </button>
                  <button
                    className="btn btn-outline-secondary px-4"
                    disabled={builderLoading}
                    onClick={() => {
                      setBuilderInitialized(false);
                      setBuilderHoldings(null);
                      setBuilderResults({ EQ_AU: null, EQ_INTL: null });
                      setBuilderError(null);
                    }}
                  >
                    Reset
                  </button>
                </div>

                {builderError && (
                  <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                    {builderError}
                  </div>
                )}

                {/* Results — follow the dropdown, show only selected SAC */}
                {renderResults(builderSac)}
              </>
            );
          })()}

          {activeTab === 'builder' && !results && (
            <div className="text-muted p-3">Load a portfolio first to use the Portfolio Builder.</div>
          )}

        </div>
      </div>

      {/* Disclaimer Card */}
      <div className="card disclaimer-card mt-3">
        <div className="card-body">
          <div className="disclaimer-text">
            <h6><i className="bi bi-info-circle"></i> Risk Model / Risk Analytics &ndash; Important Information</h6>
            <ManagedDisclaimer dkey="tool_risk_model" as="p" className="small text-muted mb-0" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskModelApp;
